// Third-party
import React from 'react';

// Utils
import { BMOi18n } from '../../../../utils/bmo-gif/constants';
import { LOCALE, formatLocalizedString, formatDateWithLocale } from '../../../../utils/i18n';
import { COMPARISON_ITEM_TYPES, YieldParamsType } from '../../../../utils/constants';
import { isGICBenchmark } from '../../../../utils/bmo-gif/utils';
import Dictionary from '@/types/dictionary';
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';

type Props = {
  items: ComparisonItem[];
  locale?: Locale;
};

const BMOGifMarketingTemplateDisclaimer = ({ items, locale }: Props) => {
  const lang = locale || LOCALE.EN_CA;
  const messages = BMOi18n[lang];

  const GIF = items.find(item => item.type === COMPARISON_ITEM_TYPES.FUND);
  if (!GIF) {
    return null;
  }

  const GIC = items.find(
    item =>
      item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && // Item is a portfolio
      item.holdings.length === 1 && // With 1 holding
      !!item.holdings[0].yieldParams && // And that holding is a custom yield
      item.holdings[0].yieldParams.type === YieldParamsType.GIC,
  );

  const dateFormat = 'LL';
  const gifName = formatLocalizedString(GIF.name, lang);
  const fundName = gifName.split('GIF')[0];
  const inception = !GIF.dates.inception ? '' : formatDateWithLocale(GIF.dates.inception, lang, dateFormat);
  const date = formatDateWithLocale(new Date(), lang, dateFormat);
  const rate = GIC && GIC.holdings[0].yieldParams ? GIC.holdings[0].yieldParams.rate.toString() : '';

  // Backtested GIF's have an * in their name
  const usesAdjustedGIF = gifName.indexOf('*') !== -1;

  const usesGICBenchmark = items.find(item => isGICBenchmark(item));

  const formatStr = (str: string) => {
    let out = str;

    const values: Dictionary<string> = {
      date,
      fundName,
      gifName,
      inception,
      rate,
    };

    Object.keys(values).forEach(key => {
      out = out.replace(new RegExp(`({{${key}}})`, 'g'), values[key]);
    });

    return out;
  };

  return (
    <div className="bmo-disclaimer">
      {usesAdjustedGIF && <span>{formatStr(messages.PROPOSAL.MARKETING_TEMPLATE.DISCLAIMERS.FUND_CONSTRUCTION.BACKTESTED)}</span>}

      {!usesAdjustedGIF && <span>{formatStr(messages.PROPOSAL.MARKETING_TEMPLATE.DISCLAIMERS.FUND_CONSTRUCTION.REGULAR)}</span>}

      {usesGICBenchmark && <span>{formatStr(messages.PROPOSAL.MARKETING_TEMPLATE.DISCLAIMERS.BENCHMARK.GIC)}</span>}

      {GIC && <span>{formatStr(messages.PROPOSAL.MARKETING_TEMPLATE.DISCLAIMERS.BENCHMARK.MANUAL_RATE)}</span>}

      <span>
        <sup>† </sup>
        {messages.PROPOSAL.MARKETING_TEMPLATE.DISCLAIMERS.GIF100100}
      </span>
      <span>{messages.PROPOSAL.MARKETING_TEMPLATE.DISCLAIMERS.GENERIC}</span>
    </div>
  );
};

export default BMOGifMarketingTemplateDisclaimer;
